import { formatDate, watchIgnorable } from '@vueuse/core'
import { computed, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import useRoles, { Role } from '@/modules/roles/composables/useRoles'

import { User } from '../interfaces/user'
import useUser from './useUser'
import useUsersSearch from './useUsersSearch'

const useEditUserForm = () => {
	const route = useRoute()

	// Crear referencia reactiva al ID del usuario para mejor reactividad
	const userId = computed(() => Number(route.params.id))
	const pendingSupervisorSelection = ref<User | null>(null)

	// Estado para roles pendientes de asignar (preview)
	const rolesPendingAssignment = reactive<Role[]>([])

	const {
		data: userEdit,
		isFetching: isFetchingUser,
		isLoading: isLoadingUser,
		refetch: refetchUser,
	} = useUser({
		immediate: true,
		params: {
			id: userId,
		},
		context: 'edit',
		queryOptions: {
			refetchOnWindowFocus: false,
		},
	})

	const {
		data: supervisor,
		isFetching: isFetchingSupervisor,
		isLoading: isLoadingSupervisor,
		refetch: refetchSupervisor,
		userId: supervisorId,
	} = useUser({
		immediate: false,
		context: 'supervisor',
		queryOptions: {
			refetchOnWindowFocus: false,
		},
	})

	// Filtros para búsqueda de supervisores
	const supervisorFilters = computed(() => {
		// Asegurarse de que siempre use los roles actualizados del usuario actual
		const currentUserRoles = userEdit.value?.roles || []
		return {
			// OJO: el endpoint espera la key `company` (no companyId) — con la key
			// equivocada la rama de supervisores del backend nunca corre y el
			// picker regresa vacío.
			company: userEdit.value?.companyId || undefined,
			supervisors: currentUserRoles,
		}
	})

	interface Form {
		id: number
		name: string
		secondName: string
		lastname: string
		secondLastname: string
		avatar: File | string
		birthDate: string
		gender: string
		companyId: number
		supervisor: User | undefined
		roles: Role[] | undefined
		// Acceso al sistema desde el panel de edición: dar acceso exige correo
		// principal (el backend lo valida igual).
		haveAccess: boolean
		email: string
	}

	// Estado inicial del formulario
	const initialFormState: Form = {
		id: 0,
		name: '',
		secondName: '',
		lastname: '',
		secondLastname: '',
		avatar: '',
		birthDate: '',
		gender: '',
		companyId: 0,
		supervisor: undefined,
		roles: undefined,
		haveAccess: false,
		email: '',
	}

	// Estado reactivo del formulario
	const form = reactive<Form>({ ...initialFormState })

	// Función para resetear el formulario
	const resetForm = () => {
		Object.assign(form, { ...initialFormState })
		// Limpiar también los roles pendientes
		rolesPendingAssignment.splice(0, rolesPendingAssignment.length)
	}

	// Función para poblar el formulario con datos del usuario
	const populateFormFromUser = (user: User) => {
		Object.assign(form, {
			id: user.id,
			name: user.name || '',
			secondName: user.secondName || '',
			lastname: user.lastname || '',
			secondLastname: user.secondLastname || '',
			avatar: user.avatar || '',
			birthDate:
				user.birthDate ?
					formatDate(new Date(user.birthDate), 'DD-MM-YYYY')
				:	'',
			gender: user.gender || '',
			companyId: user.companyId || 0,
			supervisor: undefined, // TODO: Manejar supervisor si es necesario
			roles: undefined, // Se poblará después con el watch de roles
			haveAccess: !!user.haveAccess,
			email: user.email || '',
		})
	}

	// Composable de roles con reactividad mejorada
	const {
		data: roles,
		refetch: refetchRoles,
		isLoading: isLoadingRoles,
		companyId,
	} = useRoles({
		companyId: 0,
		immediate: false,
	})

	// Composable para búsqueda de supervisores
	const {
		data: supervisors,
		isFetching: isFetchingSupervisors,
		isLoading: isLoadingSupervisors,
		refetch: refetchSupervisors,
		query: supervisorQuery,
		setQuery: setSupervisorQuery,
	} = useUsersSearch({
		immediate: false,
		resetCache: false,
		automaticSearch: false,
		params: {
			filters: supervisorFilters,
		},
	})

	// Watch principal del usuario - con control mejorado
	const { ignoreUpdates } = watchIgnorable(
		userEdit,
		(newUser, oldUser) => {
			if (!newUser) {
				resetForm()
				return
			}

			// Solo poblar si es un usuario diferente o es la primera vez
			if (!oldUser || oldUser.id !== newUser.id) {
				populateFormFromUser(newUser)

				// Actualizar companyId para refetch de roles
				if (newUser.companyId && newUser.companyId !== oldUser?.companyId) {
					companyId.value = newUser.companyId

					// Dar un tick para que companyId se actualice antes del refetch
					refetchRoles()
				}

				// Manejar supervisor
				if (newUser.supervisorId) {
					// Si tiene supervisorId, hacer refetch
					supervisorId.value = newUser.supervisorId
					refetchSupervisor()
				} else {
					// Si NO tiene supervisorId, resetear supervisor
					form.supervisor = undefined
				}
			}
		},
		{
			immediate: true,
			deep: false, // No necesitamos deep watch para el objeto user completo
		}
	)

	// Función para mapear roles del usuario
	const mapRolesToForm = () => {
		const currentRoles = roles.value
		const currentUser = userEdit.value

		if (!currentRoles?.length) {
			return
		}

		if (!currentUser?.roles?.length) {
			form.roles = []
			return
		}

		// Mapear roles del usuario con roles disponibles
		const userHasRoles = currentRoles.filter((availableRole) => {
			const isMatched = currentUser.roles?.some((userRole: any) => {
				const userRoleName =
					typeof userRole === 'string' ? userRole : userRole.name
				const matches = userRoleName === availableRole.name

				return matches
			})

			return isMatched
		})

		// Actualizar sin causar loops
		form.roles = userHasRoles
	}

	// Función para agregar rol a la preview sin modificar form.roles.
	// Modelo de UN solo rol: elegir un rol REEMPLAZA cualquier pendiente anterior
	// (es un rol o es otro, nunca los dos juntos).
	const addRoleToPending = (role: Role) => {
		const isAlreadyAssigned = form.roles?.some((r) => r.id === role.id)

		if (!isAlreadyAssigned) {
			rolesPendingAssignment.splice(0, rolesPendingAssignment.length, role)
		}
	}

	// Función para quitar rol de la preview
	const removeRoleFromPending = (roleId: number) => {
		const index = rolesPendingAssignment.findIndex((r) => r.id === roleId)
		if (index > -1) {
			rolesPendingAssignment.splice(index, 1)
		}
	}

	// Función para aplicar los roles pendientes al formulario.
	// Un solo rol por usuario: el pendiente REEMPLAZA al rol actual, no se suma.
	const applyPendingRoles = () => {
		if (rolesPendingAssignment.length > 0) {
			form.roles = [...rolesPendingAssignment]

			// Limpiar roles pendientes después de aplicar
			rolesPendingAssignment.splice(0, rolesPendingAssignment.length)
		}
	}

	// Función para limpiar roles pendientes
	const clearPendingRoles = () => {
		rolesPendingAssignment.splice(0, rolesPendingAssignment.length)
	}

	// Watch de roles mejorado
	watch(
		[() => roles.value, () => userEdit.value],
		() => {
			mapRolesToForm()
		},
		{
			immediate: true,
		}
	)

	// Watch específico para companyId - forzar refetch cuando cambie
	watch(
		() => userEdit.value?.companyId,
		async (newCompanyId, oldCompanyId) => {
			if (newCompanyId && newCompanyId !== oldCompanyId) {
				// Asegurar que companyId esté actualizado
				companyId.value = newCompanyId

				// Refetch roles para la nueva compañía
				await refetchRoles()

				// Dar un pequeño tiempo para que los roles se carguen
				mapRolesToForm()

				// También refetch supervisores con los nuevos filtros
				await refetchSupervisors()
			}
		},
		{ immediate: false }
	)

	// Watch para supervisorId - cargar supervisor cuando cambie el usuario
	watch(
		() => userEdit.value?.supervisorId,
		async (newSupervisorId, oldSupervisorId) => {
			if (newSupervisorId && newSupervisorId !== oldSupervisorId) {
				supervisorId.value = newSupervisorId
				await refetchSupervisor()
			} else if (!newSupervisorId) {
				// Si el nuevo usuario no tiene supervisor, limpiar
				form.supervisor = undefined
			}
		},
		{ immediate: false }
	)

	// Watch para los roles del usuario - refetch supervisores cuando cambien los roles
	watch(
		() => userEdit.value?.roles,
		async (newRoles, oldRoles) => {
			// Solo refetch si los roles realmente cambiaron y hay roles
			if (newRoles && JSON.stringify(newRoles) !== JSON.stringify(oldRoles)) {
				await refetchSupervisors()
			}
		},
		{ immediate: false, deep: true }
	)

	// Watch para sincronizar supervisor del backend con el formulario
	watch(
		supervisor,
		(newSupervisor) => {
			// Actualizar form.supervisor cuando:
			// 1. No hay supervisor en el form (primera carga)
			// 2. El supervisor del backend cambió y coincide con el ID actual del form
			if (newSupervisor) {
				if (!form.supervisor) {
					// Primera carga
					form.supervisor = newSupervisor
				} else if (form.supervisor.id === newSupervisor.id) {
					// Actualizar con los datos más recientes del backend
					form.supervisor = newSupervisor
				}
			}
		},
		{ immediate: true }
	)

	// Computed properties mejoradas
	const isFormReady = computed(
		() => !isLoadingUser.value && !isFetchingUser.value && !!userEdit.value
	)

	const isRolesReady = computed(() => !isLoadingRoles.value && !!roles.value)

	const hasFormChanges = computed(() => {
		if (!userEdit.value) return false

		const changes = {
			name: form.name !== (userEdit.value.name || ''),
			secondName: form.secondName !== (userEdit.value.secondName || ''),
			lastname: form.lastname !== (userEdit.value.lastname || ''),
			secondLastname:
				form.secondLastname !== (userEdit.value.secondLastname || ''),
			gender: form.gender !== (userEdit.value.gender || ''),
			haveAccess: form.haveAccess !== !!userEdit.value.haveAccess,
			email: form.email !== (userEdit.value.email || ''),
			birthDate:
				form.birthDate !==
				(userEdit.value.birthDate ?
					formatDate(new Date(userEdit.value.birthDate), 'DD-MM-YYYY')
				:	''),
			supervisor: (() => {
				// Comparar el ID del supervisor con form.supervisor, no con pendingSupervisorSelection
				const currentSupervisorId = userEdit.value?.supervisorId
				const formSupervisorId =
					pendingSupervisorSelection.value?.id || form.supervisor?.id

				// Si ambos son undefined/null, no hay cambios
				if (!currentSupervisorId && !formSupervisorId) return false

				// Si uno es undefined/null y el otro no, hay cambios
				if (!currentSupervisorId || !formSupervisorId) return true

				// Comparar los IDs
				return currentSupervisorId !== formSupervisorId
			})(),
			roles: (() => {
				const userRoles = userEdit.value?.roles || []
				const formRoles = form.roles || []

				if (userRoles.length !== formRoles.length) return true

				const allMatch = formRoles.every((formRole) =>
					userRoles.some((userRole: any) => {
						const userRoleName =
							typeof userRole === 'string' ? userRole : userRole.name
						return userRoleName === formRole.name
					})
				)

				return !allMatch
			})(),
		}

		return Object.values(changes).some(Boolean)
	})

	const hasFormChangesRoles = computed(() => {
		if (!userEdit.value) return false

		return rolesPendingAssignment.some((pendingRole) => {
			return !(userEdit.value?.roles || []).some((userRole: any) => {
				const userRoleName =
					typeof userRole === 'string' ? userRole : userRole.name
				return userRoleName === pendingRole.name
			})
		})
	})

	return {
		// Estado del formulario
		form,
		rolesPendingAssignment,
		pendingSupervisorSelection,

		// Estado de carga
		isLoadingUser,
		isFetchingUser,
		isLoadingRoles,
		isFormReady,
		isRolesReady,

		// Datos
		userEdit,
		roles,

		// Datos de supervisor
		supervisor,
		supervisorId,
		supervisors,
		isFetchingSupervisor,
		isLoadingSupervisor,
		isFetchingSupervisors,
		isLoadingSupervisors,
		supervisorQuery,
		setSupervisorQuery,
		refetchSupervisor,
		refetchSupervisors,

		// Estados derivados
		hasFormChangesRoles,
		hasFormChanges,
		userId,

		// Funciones utilitarias
		resetForm,
		populateFormFromUser,
		refetchRoles,
		refetchUser,

		// Funciones para roles pendientes
		addRoleToPending,
		removeRoleFromPending,
		applyPendingRoles,
		clearPendingRoles,

		// Control avanzado
		ignoreUpdates,
	}
}

export default useEditUserForm
